// Marketing-attribution snapshot taken once at widget mount and
// forwarded on Schedule / Waitlist to SF (Dhruv 2026-06-13 deploy).
//
// We populate every field SF accepts that is derivable from the
// embedding page's URL / referrer / configured client id. Fields not
// captured here (hearAbout survey answers, TV-attribution fields)
// remain undefined — Dhruv confirmed unknown fields can be omitted.

import type { MarketingPayload } from '../types';

const MOBILE_MAX_WIDTH = 1023;

/**
 * Persist a stable per-session submissionId. We mint one on first
 * capture and stash it in sessionStorage so repeated bookings in the
 * same tab/session share an id. Falls back to a per-call uuid if
 * storage is blocked.
 */
function getOrCreateSubmissionId(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();
  const key = 'cc_booking_submission_id';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.sessionStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Read a specific cookie by name. Returns undefined when absent or
 * when document.cookie is unavailable (cross-origin iframe, etc.).
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Read the GA4 `_ga` cookie's client id portion. Format:
 *   GA1.<version>.<client-id>.<timestamp>
 * Returns the last two segments joined ("<client-id>.<timestamp>")
 * which is the value GA4 / sGTM treat as the client id.
 *
 * Falls back to undefined if the cookie isn't readable (cross-origin
 * iframe, blocked cookies, etc.).
 */
function readGaClientId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return undefined;
  const parts = match[1].split('.');
  if (parts.length < 4) return undefined;
  return `${parts[2]}.${parts[3]}`;
}

export function captureMarketing(): MarketingPayload {
  if (typeof window === 'undefined') return {};

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const pickParam = (key: string): string | undefined => {
    const v = params.get(key);
    return v && v.length > 0 ? v : undefined;
  };

  const queryString = url.search.startsWith('?') ? url.search.slice(1) : url.search;
  const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
  const configClientId = window.CC_WIDGET_CONFIG?.channelmixClientId;

  // Meta browser identifiers — fbp is set by the Meta pixel on page
  // load; fbc is set when the visitor lands via a Meta ad and the
  // pixel converts fbclid to the fbc format. If the visitor hits the
  // page with ?fbclid=… and the Meta pixel hasn't set _fbc yet, we
  // synthesize the value here so CAPI still gets attribution.
  const fbp = readCookie('_fbp');
  let fbc = readCookie('_fbc');
  if (!fbc) {
    const fbclid = pickParam('fbclid');
    if (fbclid) {
      // Meta's documented synthesis: fb.<subdomainIndex>.<creationTime>.<fbclid>
      // subdomainIndex=1 (root domain), creationTime=ms since epoch.
      fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
  }

  return {
    submissionId: getOrCreateSubmissionId(),
    pageUrl: window.location.href || undefined,
    referrerPage: document.referrer || undefined,
    marketingPageCode: queryString.length > 0 ? queryString : undefined,
    utmSource: pickParam('utm_source'),
    utmMedium: pickParam('utm_medium'),
    utmCampaign: pickParam('utm_campaign'),
    utmTerm: pickParam('utm_term'),
    utmContent: pickParam('utm_content'),
    utmGclid: pickParam('gclid'),
    cjevent: pickParam('cjevent'),
    clientId: configClientId || readGaClientId(),
    bookedByType: 'Customer',
    websiteConfirmationDevice: isMobile ? 'Mobile' : 'Desktop',
    fbp,
    fbc,
  };
}
